import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Page, PageHeader } from "@/components/PageHeader";
import {
  PhoneIncoming, PhoneOutgoing, Clock, Phone, Waveform, Copy, CheckCircle,
  Sparkle, PlusCircle, CaretDown, WarningCircle, BookBookmark, LinkSimple, ArrowClockwise, FloppyDisk,
  UploadSimple, Trash, FileText, Globe, Key,
} from "@phosphor-icons/react";
import { timeAgo } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const tagStyle = {
  converted: "bg-success/10 text-success border-success/20",
  interested: "bg-blue-50 text-blue-600 border-blue-200",
  resolved: "bg-success/10 text-success border-success/20",
  escalated: "bg-destructive/10 text-destructive border-destructive/20",
  follow_up_needed: "bg-amber-50 text-amber-600 border-amber-200",
  completed: "bg-secondary text-muted-foreground border-border",
};

const fmtDur = (s) => `${Math.floor((s || 0) / 60)}:${String((s || 0) % 60).padStart(2, "0")}`;

export default function Voice() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: calls = [] } = useQuery({ queryKey: ["calls"], queryFn: async () => (await api.get("/calls")).data });
  const { data: hookLogs = [] } = useQuery({
    queryKey: ["voice-webhook-logs"],
    queryFn: async () => (await api.get("/voice/webhook-logs?limit=20")).data,
    refetchInterval: 30000,
  });

  const totalDur = calls.reduce((a, c) => a + (c.duration || c.duration_seconds || 0), 0);
  const resolved = calls.filter((c) => c.outcome === "resolved" || c.tag === "converted").length;

  return (
    <Page>
      <PageHeader title="Voice" subtitle="Grok Voice Agent — inbound & outbound calls auto-logged to guest profiles.">
        <div className="flex items-center gap-2">
          <SimulateCallDialog onDone={() => qc.invalidateQueries({ queryKey: ["calls"] })} />
        </div>
      </PageHeader>

      {/* Webhook activity (live debug feed) */}
      <WebhookActivity logs={hookLogs} />

      {/* stats */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="rounded-lg border border-border bg-card p-4 flex items-center gap-3"><span className="h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center"><PhoneIncoming size={17} weight="fill" /></span><div><div className="font-heading text-xl font-semibold" data-testid="voice-total-calls">{calls.length}</div><div className="text-xs text-muted-foreground">Total calls</div></div></div>
        <div className="rounded-lg border border-border bg-card p-4 flex items-center gap-3"><span className="h-9 w-9 rounded-md bg-emerald-50 text-emerald-600 flex items-center justify-center"><CheckCircle size={17} weight="fill" /></span><div><div className="font-heading text-xl font-semibold">{resolved}</div><div className="text-xs text-muted-foreground">Resolved / converted</div></div></div>
        <div className="rounded-lg border border-border bg-card p-4 flex items-center gap-3"><span className="h-9 w-9 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center"><Clock size={17} weight="fill" /></span><div><div className="font-heading text-xl font-semibold">{calls.length ? fmtDur(Math.round(totalDur / calls.length)) : "0:00"}</div><div className="text-xs text-muted-foreground">Avg duration</div></div></div>
      </div>

      {/* call log */}
      <div className="space-y-3">
        {calls.map((c, i) => <CallRow key={c.id} c={c} i={i} />)}
        {calls.length === 0 && (
          <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            No calls yet. Calls placed to your Grok number will appear here — or use “Simulate a call” to test the flow.
          </div>
        )}
      </div>
    </Page>
  );
}

export function VoiceKnowledge() {
  const qc = useQueryClient();
  const { data: kb } = useQuery({ queryKey: ["voice-knowledge"], queryFn: async () => (await api.get("/voice/knowledge")).data });
  const [open, setOpen] = useState(true);
  const [f, setF] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sample, setSample] = useState({ arrival_date: "2026-09-12", departure_date: "2026-09-15", adults: 2 });
  const [newUrl, setNewUrl] = useState("");
  const docRef = useRef(null);

  if (kb && f === null) {
    setF({
      custom_info: kb.custom_info || "",
      booking_link_template: kb.booking_link_template || "",
      booking_property_id: kb.booking_defaults?.property_id || "",
      booking_code: kb.booking_defaults?.booking_code || "",
      booking_date_format: kb.booking_defaults?.date_format || "YYYY-MM-DD",
    });
  }
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const sources = kb?.sources || [];

  const save = useMutation({
    mutationFn: async () => (await api.put("/voice/knowledge", f)).data,
    onSuccess: (data) => { qc.setQueryData(["voice-knowledge"], data); toast.success("Voice agent knowledge saved"); },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });
  const addUrl = useMutation({
    mutationFn: async () => (await api.post("/voice/knowledge/sources/url", { url: newUrl.trim() })).data,
    onSuccess: (data) => {
      qc.setQueryData(["voice-knowledge"], data);
      setNewUrl("");
      if (data.source_error) toast.error(data.source_error);
      else toast.success("Web page added & indexed");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });
  const addDoc = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post("/voice/knowledge/sources/document", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (data) => { qc.setQueryData(["voice-knowledge"], data); toast.success("Document added & indexed"); },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });
  const refreshSource = useMutation({
    mutationFn: async (sid) => (await api.post(`/voice/knowledge/sources/${sid}/refresh`)).data,
    onSuccess: (data) => {
      qc.setQueryData(["voice-knowledge"], data);
      if (data.source_error) toast.error(data.source_error);
      else toast.success("Re-indexed");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });
  const deleteSource = useMutation({
    mutationFn: async (sid) => (await api.delete(`/voice/knowledge/sources/${sid}`)).data,
    onSuccess: (data) => { qc.setQueryData(["voice-knowledge"], data); toast.success("Source removed"); },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });

  const onDoc = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10 MB)"); return; }
    addDoc.mutate(file);
    if (docRef.current) docRef.current.value = "";
  };

  const previewLink = () => {
    const t = f.booking_link_template;
    if (!t) { setPreview("No booking link template set yet."); return; }
    const applyFmt = (iso, key) => {
      if (!iso) return "";
      const [y, mo, d] = iso.split("-");
      switch (key) {
        case "MM/DD/YYYY": return `${mo}/${d}/${y}`;
        case "MM-DD-YYYY": return `${mo}-${d}-${y}`;
        case "DD/MM/YYYY": return `${d}/${mo}/${y}`;
        case "DD-MM-YYYY": return `${d}-${mo}-${y}`;
        case "YYYYMMDD": return `${y}${mo}${d}`;
        default: return `${y}-${mo}-${d}`;
      }
    };
    const vals = {
      property_id: f.booking_property_id || "",
      arrival_date: applyFmt(sample.arrival_date, f.booking_date_format),
      departure_date: applyFmt(sample.departure_date, f.booking_date_format),
      adults: sample.adults || "",
      booking_code: f.booking_code || "",
    };
    setPreview(t.replace(/\{(property_id|arrival_date|departure_date|adults|booking_code)\}/g,
      (_, k) => (vals[k] !== "" && vals[k] != null ? encodeURIComponent(vals[k]) : "")));
  };

  if (!f) return null;
  const busy = addUrl.isPending || addDoc.isPending;

  return (
    <div className="rounded-lg border border-border bg-card p-4 mb-4" data-testid="voice-knowledge-card">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between" data-testid="voice-knowledge-toggle">
        <div className="flex items-center gap-2">
          <span className="h-8 w-8 rounded-md bg-primary/10 text-primary flex items-center justify-center"><BookBookmark size={16} weight="fill" /></span>
          <div className="text-left">
            <div className="font-heading font-semibold text-sm">Voice agent knowledge</div>
            <div className="text-xs text-muted-foreground">What Carina knows — custom info, web pages, documents & booking link</div>
          </div>
        </div>
        <CaretDown size={16} className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          <div>
            <Label className="text-xs flex items-center gap-1.5"><BookBookmark size={13} /> Custom information</Label>
            <Textarea value={f.custom_info} onChange={(e) => upd("custom_info", e.target.value)} rows={5}
              placeholder="Check-in/out times, amenities, pet & parking policy, cancellation terms, anything the agent should know…"
              data-testid="kb-custom-info" className="mt-1.5 text-sm" />
          </div>

          {/* Knowledge sources: web pages + documents */}
          <div>
            <Label className="text-xs flex items-center gap-1.5"><FileText size={13} /> Knowledge sources</Label>
            <div className="mt-1.5 space-y-2" data-testid="kb-sources-list">
              {sources.length === 0 && (
                <p className="text-[11px] text-muted-foreground">Add FAQ/policy web pages or upload PDF/DOCX documents — the agent reads them to answer questions.</p>
              )}
              {sources.map((s) => (
                <div key={s.id} className="flex items-center gap-2.5 rounded-md border border-border px-3 py-2" data-testid={`kb-source-${s.id}`}>
                  <span className={`h-7 w-7 rounded-md flex items-center justify-center shrink-0 ${s.type === "url" ? "bg-blue-50 text-blue-600" : "bg-violet-50 text-violet-600"}`}>
                    {s.type === "url" ? <Globe size={14} weight="fill" /> : <FileText size={14} weight="fill" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{s.label}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {s.error ? <span className="text-destructive">{s.error}</span>
                        : `${s.chars?.toLocaleString() || 0} chars indexed${s.fetched_at ? ` · ${new Date(s.fetched_at).toLocaleDateString()}` : ""}`}
                    </div>
                  </div>
                  {s.type === "url" && (
                    <button title="Re-index" disabled={refreshSource.isPending} onClick={() => refreshSource.mutate(s.id)} data-testid={`kb-source-refresh-${s.id}`} className="text-muted-foreground hover:text-foreground shrink-0">
                      <ArrowClockwise size={15} className={refreshSource.isPending && refreshSource.variables === s.id ? "animate-spin" : ""} />
                    </button>
                  )}
                  <button title="Remove" disabled={deleteSource.isPending} onClick={() => deleteSource.mutate(s.id)} data-testid={`kb-source-delete-${s.id}`} className="text-muted-foreground hover:text-destructive shrink-0">
                    <Trash size={15} />
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-2.5 flex items-center gap-2">
              <Input value={newUrl} onChange={(e) => setNewUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && newUrl.trim()) addUrl.mutate(); }}
                placeholder="https://yourhotel.com/faq" data-testid="kb-add-url-input" className="flex-1 text-sm" />
              <Button type="button" variant="outline" size="sm" disabled={!newUrl.trim() || busy} onClick={() => addUrl.mutate()} data-testid="kb-add-url-btn" className="shrink-0">
                <PlusCircle size={14} className="mr-1" /> {addUrl.isPending ? "Adding…" : "Add URL"}
              </Button>
              <input ref={docRef} type="file" accept=".pdf,.docx,.txt,.md,.csv" onChange={onDoc} className="hidden" data-testid="kb-doc-input" />
              <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => docRef.current?.click()} data-testid="kb-upload-doc-btn" className="shrink-0">
                <UploadSimple size={14} className="mr-1" /> {addDoc.isPending ? "Uploading…" : "Upload doc"}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Note: some sites (e.g. large hotel brands) block automated access — if a URL fails, upload a PDF/DOCX or use Custom information instead.</p>
          </div>

          <div className="rounded-md border border-border p-3.5 bg-secondary/40">
            <Label className="text-xs flex items-center gap-1.5"><LinkSimple size={13} /> Booking link template</Label>
            <Input value={f.booking_link_template} onChange={(e) => upd("booking_link_template", e.target.value)}
              placeholder="https://book.yourhotel.com/reserve?propertyId={property_id}&arrival={arrival_date}&departure={departure_date}&adults={adults}&code={booking_code}"
              data-testid="kb-booking-template" className="mt-1.5 text-xs font-mono" />
            <p className="text-[11px] text-muted-foreground mt-1.5">
              Use these variables — the agent fills them from the call: <code className="text-foreground">{"{property_id}"}</code> <code className="text-foreground">{"{arrival_date}"}</code> <code className="text-foreground">{"{departure_date}"}</code> <code className="text-foreground">{"{adults}"}</code> <code className="text-foreground">{"{booking_code}"}</code>
            </p>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div><Label className="text-[11px]">Default property ID</Label><Input value={f.booking_property_id} onChange={(e) => upd("booking_property_id", e.target.value)} placeholder="e.g. WREN-001" data-testid="kb-booking-propid" className="mt-1 text-sm" /></div>
              <div><Label className="text-[11px]">Default booking code</Label><Input value={f.booking_code} onChange={(e) => upd("booking_code", e.target.value)} placeholder="e.g. VOICE10 (optional)" data-testid="kb-booking-code" className="mt-1 text-sm" /></div>
            </div>
            <div className="mt-3">
              <Label className="text-[11px]">Date format in the link</Label>
              <Select value={f.booking_date_format} onValueChange={(v) => upd("booking_date_format", v)}>
                <SelectTrigger className="mt-1 h-9 text-sm w-56" data-testid="kb-date-format"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="YYYY-MM-DD">2026-09-12 (YYYY-MM-DD)</SelectItem>
                  <SelectItem value="MM/DD/YYYY">09/12/2026 (MM/DD/YYYY)</SelectItem>
                  <SelectItem value="MM-DD-YYYY">09-12-2026 (MM-DD-YYYY)</SelectItem>
                  <SelectItem value="DD/MM/YYYY">12/09/2026 (DD/MM/YYYY)</SelectItem>
                  <SelectItem value="DD-MM-YYYY">12-09-2026 (DD-MM-YYYY)</SelectItem>
                  <SelectItem value="YYYYMMDD">20260912 (YYYYMMDD)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">The agent collects the date once; we format <code className="text-foreground">{"{arrival_date}"}</code> / <code className="text-foreground">{"{departure_date}"}</code> to match your booking engine.</p>
            </div>

            <div className="mt-3 border-t border-border pt-3">
              <Label className="text-[11px] text-muted-foreground">Preview a generated link</Label>
              <div className="flex flex-wrap items-end gap-2 mt-1.5">
                <div><span className="text-[10px] text-muted-foreground">Arrival</span><Input type="date" value={sample.arrival_date} onChange={(e) => setSample((s) => ({ ...s, arrival_date: e.target.value }))} className="h-8 text-xs w-36" data-testid="kb-preview-arrival" /></div>
                <div><span className="text-[10px] text-muted-foreground">Departure</span><Input type="date" value={sample.departure_date} onChange={(e) => setSample((s) => ({ ...s, departure_date: e.target.value }))} className="h-8 text-xs w-36" data-testid="kb-preview-departure" /></div>
                <div><span className="text-[10px] text-muted-foreground">Adults</span><Input type="number" min="1" value={sample.adults} onChange={(e) => setSample((s) => ({ ...s, adults: e.target.value }))} className="h-8 text-xs w-20" data-testid="kb-preview-adults" /></div>
                <Button type="button" variant="secondary" size="sm" disabled={!f.booking_link_template} onClick={previewLink} data-testid="kb-preview-btn">Preview</Button>
              </div>
              {preview && <div className="mt-2 text-[11px] break-all bg-background border border-border rounded-md p-2 font-mono" data-testid="kb-preview-result">{preview}</div>}
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="kb-save-btn"><FloppyDisk size={15} className="mr-1.5" /> {save.isPending ? "Saving…" : "Save knowledge"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function WebhookActivity({ logs }) {
  const [open, setOpen] = useState(true);
  const resultStyle = {
    accepted: "bg-success/10 text-success border-success/20",
    ignored: "bg-secondary text-muted-foreground border-border",
    rejected: "bg-destructive/10 text-destructive border-destructive/20",
  };
  return (
    <div className="rounded-lg border border-border bg-card p-4 mb-4" data-testid="webhook-activity-card">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between" data-testid="webhook-activity-toggle">
        <div className="flex items-center gap-2">
          <span className="h-8 w-8 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center"><Waveform size={16} weight="fill" /></span>
          <div className="text-left">
            <div className="font-heading font-semibold text-sm">Webhook activity</div>
            <div className="text-xs text-muted-foreground">Live feed of what xAI sends to /api/voice/incoming · refreshes every 30s</div>
          </div>
        </div>
        <CaretDown size={16} className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-1.5" data-testid="webhook-activity-list">
          {logs.length === 0 && (
            <div className="rounded-md border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
              No webhook events received yet. If a call doesn't show here, the number's webhook in the xAI console isn't pointed at this URL.
            </div>
          )}
          {logs.map((l) => (
            <div key={l.id} className="flex items-start gap-3 rounded-md border border-border px-3 py-2 text-xs">
              <span className={`shrink-0 px-1.5 py-0.5 rounded border font-medium capitalize ${resultStyle[l.result] || resultStyle.ignored}`}>{l.result}</span>
              <div className="flex-1 min-w-0">
                <div className="text-foreground">{l.detail}</div>
                <div className="text-muted-foreground mt-0.5 flex flex-wrap gap-x-3">
                  {l.from && <span>from {l.from}</span>}
                  {l.to && <span>to {l.to}</span>}
                  {l.signature && <span>sig: {l.signature}</span>}
                  {l.event_type && <span>{l.event_type}</span>}
                </div>
              </div>
              <span className="text-muted-foreground shrink-0">{new Date(l.ts).toLocaleTimeString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CallRow({ c, i }) {
  const [open, setOpen] = useState(false);
  const tag = c.tag || c.outcome;
  return (
    <div data-testid={`call-row-${i}`} className="fade-up rounded-lg border border-border bg-card p-4" style={{ animationDelay: `${i * 45}ms` }}>
      <div className="flex items-start gap-4">
        <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${c.direction === "inbound" ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"}`}>
          {c.direction === "inbound" ? <PhoneIncoming size={18} weight="fill" /> : <PhoneOutgoing size={18} weight="fill" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {c.guest_id ? (
              <Link to={`/app/guests/${c.guest_id}`} className="font-medium text-sm hover:text-primary hover:underline" data-testid={`call-guest-link-${i}`}>{c.guest_name}</Link>
            ) : (
              <span className="font-medium text-sm">{c.guest_name}</span>
            )}
            {c.phone_number && <span className="text-xs text-muted-foreground">· {c.phone_number}</span>}
            <span className="text-xs text-muted-foreground capitalize">· {c.direction}</span>
            {tag && <span className={`text-[11px] px-2 py-0.5 rounded-full border capitalize ${tagStyle[tag] || tagStyle.completed}`}>{String(tag).replace(/_/g, " ")}</span>}
            {c.source === "simulated" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-200">simulated</span>}
          </div>
          <p className="text-sm text-foreground mt-1">{c.summary || c.transcript}</p>
          <div className="flex items-center gap-3 mt-1.5">
            <span className="text-xs text-muted-foreground">{fmtDur(c.duration || c.duration_seconds)} · {timeAgo(c.created_at)}</span>
            {c.transcript && c.summary && (
              <button onClick={() => setOpen((o) => !o)} data-testid={`call-transcript-toggle-${i}`} className="text-xs text-primary hover:underline flex items-center gap-0.5">
                <CaretDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} /> Transcript
              </button>
            )}
          </div>
          {open && c.transcript && (
            <pre data-testid={`call-transcript-${i}`} className="mt-2 text-xs text-muted-foreground bg-secondary border border-border rounded-md p-3 whitespace-pre-wrap font-sans leading-relaxed">{c.transcript}</pre>
          )}
        </div>
      </div>
    </div>
  );
}

export function RegisterNumberDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const mut = useMutation({
    mutationFn: async () => (await api.post("/voice/register-number", { phone_number: phone, name })).data,
    onSuccess: () => { toast.success("Number registered with Grok Voice"); setOpen(false); setPhone(""); setName(""); onDone(); },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="register-number-btn"><PlusCircle size={15} className="mr-1.5" /> Register number</Button>
      </DialogTrigger>
      <DialogContent data-testid="register-number-dialog">
        <DialogHeader>
          <DialogTitle>Register a Grok Voice number</DialogTitle>
          <DialogDescription>Registers a BYO SIP number with xAI and wires its webhook to this property.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div><Label className="text-xs">Phone number (E.164)</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+18005550199" data-testid="reg-phone-input" className="mt-1" /></div>
          <div><Label className="text-xs">Label (optional)</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Front desk line" data-testid="reg-name-input" className="mt-1" /></div>
        </div>
        <DialogFooter>
          <Button disabled={!phone.trim() || mut.isPending} onClick={() => mut.mutate()} data-testid="reg-submit-btn">{mut.isPending ? "Registering…" : "Register"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function UpdateSecretDialog({ numbers = [], onDone }) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [secret, setSecret] = useState("");
  const single = numbers.length === 1;
  const mut = useMutation({
    mutationFn: async () => (await api.put("/voice/signing-secret", {
      signing_secret: secret,
      phone_number: (phone.trim() || (single ? numbers[0].phone_number : "")) || undefined,
    })).data,
    onSuccess: () => { toast.success("Signing secret updated"); setOpen(false); setSecret(""); setPhone(""); onDone && onDone(); },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="update-secret-btn" className="gap-1.5"><Key size={15} /> Update signing secret</Button>
      </DialogTrigger>
      <DialogContent data-testid="update-secret-dialog">
        <DialogHeader>
          <DialogTitle>Update dispatch signing secret</DialogTitle>
          <DialogDescription>Paste the <code>dispatch_signing_secret</code> xAI shows for your number to (re)enable webhook signature verification. Use this after rotating the secret or configuring the webhook manually on xAI.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label className="text-xs">Phone number (E.164){single ? " — optional" : ""}</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder={single ? numbers[0].phone_number : "+18005550199"}
              data-testid="secret-phone-input" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">Signing secret</Label>
            <Input type="password" value={secret} onChange={(e) => setSecret(e.target.value)}
              placeholder="whsec_…" data-testid="secret-value-input" className="mt-1 font-mono" />
          </div>
        </div>
        <DialogFooter>
          <Button disabled={!secret.trim() || mut.isPending} onClick={() => mut.mutate()} data-testid="secret-submit-btn">{mut.isPending ? "Saving…" : "Save secret"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SimulateCallDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    phone_number: "+14155550142", name: "", email: "", direction: "inbound",
    outcome: "resolved", duration_seconds: 150,
    transcript: "Caller: Hi, I'd like to check availability for next weekend.\nAgent: Of course! For which dates?\nCaller: Friday to Sunday, two guests.\nAgent: We have an ocean-view king available. Shall I hold it?",
    summary: "Availability inquiry for next weekend; ocean-view king offered.",
  });
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const mut = useMutation({
    mutationFn: async () => (await api.post("/voice/simulate", { ...f, duration_seconds: Number(f.duration_seconds), tags: [f.outcome] })).data,
    onSuccess: () => { toast.success("Call simulated & logged to CRM"); setOpen(false); onDone(); },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="simulate-call-btn"><Sparkle size={15} weight="fill" className="mr-1.5" /> Simulate a call</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="simulate-call-dialog">
        <DialogHeader>
          <DialogTitle>Simulate an inbound call</DialogTitle>
          <DialogDescription>Runs the same CRM tool handlers a live Grok call would — upserts the contact (voice consent opted-in) and logs the call.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1 max-h-[60vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Caller phone</Label><Input value={f.phone_number} onChange={(e) => upd("phone_number", e.target.value)} data-testid="sim-phone" className="mt-1" /></div>
            <div><Label className="text-xs">Direction</Label>
              <Select value={f.direction} onValueChange={(v) => upd("direction", v)}>
                <SelectTrigger className="mt-1" data-testid="sim-direction"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="inbound">Inbound</SelectItem><SelectItem value="outbound">Outbound</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Name (optional)</Label><Input value={f.name} onChange={(e) => upd("name", e.target.value)} placeholder="Collected on call" data-testid="sim-name" className="mt-1" /></div>
            <div><Label className="text-xs">Email (optional)</Label><Input value={f.email} onChange={(e) => upd("email", e.target.value)} placeholder="Collected on call" data-testid="sim-email" className="mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Outcome</Label>
              <Select value={f.outcome} onValueChange={(v) => upd("outcome", v)}>
                <SelectTrigger className="mt-1" data-testid="sim-outcome"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="escalated">Escalated</SelectItem>
                  <SelectItem value="follow_up_needed">Follow-up needed</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Duration (sec)</Label><Input type="number" value={f.duration_seconds} onChange={(e) => upd("duration_seconds", e.target.value)} data-testid="sim-duration" className="mt-1" /></div>
          </div>
          <div><Label className="text-xs">Summary</Label><Input value={f.summary} onChange={(e) => upd("summary", e.target.value)} data-testid="sim-summary" className="mt-1" /></div>
          <div><Label className="text-xs">Transcript</Label><Textarea value={f.transcript} onChange={(e) => upd("transcript", e.target.value)} rows={5} data-testid="sim-transcript" className="mt-1" /></div>
        </div>
        <DialogFooter>
          <Button disabled={!f.phone_number.trim() || !f.transcript.trim() || mut.isPending} onClick={() => mut.mutate()} data-testid="sim-submit-btn">{mut.isPending ? "Logging…" : "Simulate & log"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
